import { BaseModel } from "./BaseModel";
import { IAutoplaySettings } from "../server/data/IAutoplayData";
import LocalData from "../../../core/src/utils/LocalData";
import LocalStorageVars from "../../src/const/LocalStorageVars";
import App from "../../../core/src/App";
import GameEvents from "../const/GameEvents";
import BetModel from "./BetModel";
import GameModel from "./GameModel";
import Utils from '../utils/Utils';

class AutoplayModel extends BaseModel<IAutoplaySettings> {
    public lossLimits: number[] = [];
    public singleWinLimits: number[] = [];
    public totalWinLimits: number[] = [];
    public spinPresets: number[] = [];
    public autoSpinNum: number = 0;
    public lossLimit: number = 0;
    public winLimit: number = 0;
    public totalWinLimit: number = 0;
    public autospinStartBalance: number;
    public regulationSettings:{[key: string]: AutoplaySettings} = {
        'unregulated': {
            lossLimitRequired: false
        },
        'uk': {
            lossLimitRequired: true
        }
    };
    public stopOnAnyWin: boolean = false;
    public stopOnBigWin: boolean = false;
    public stopOnFeatureWin: boolean = false;
    public autoSpinLimitReached: 'total_win' | 'win' | 'loss' | 'any_win' | 'feature' | 'big_win' | null;
    public initialAutospinNum: number;

    protected _autospinStarted: boolean;

    constructor() {
        super();
        this.autoSpinNum = 0;
        // this.tmpLossLimit = LocalData.getNumber(LocalStorageVars.LOSS_LIMIT);
        // this.tmpWinLimit = LocalData.getNumber(LocalStorageVars.WIN_LIMIT);

    }

    public setAutoSpin(autoSpinNum: number)
    {
        this.autospinStartBalance = BetModel.balance;
        this._autospinStarted = false;
        this.initialAutospinNum = autoSpinNum;
        this.autoSpinNum = autoSpinNum;
    }

    public startAutoSpin() {
        this._autospinStarted = true;
    }

    public decrementAutoSpinNum()
    {
        if (this.autoSpinNum > 0)
        {
            this.autoSpinNum--;
        }
        else
        {
            this._autospinStarted = false;
        }
    }

    public getAutoSpinNum()
    {
        return this.autoSpinNum;
    }


    public isAutoSpinActive()
    {
        return this.autoSpinNum > 0 && this._autospinStarted;
    }

    /**
     * Get loss limit for autospin used for UK market
     * @returns {Number}
     */
    public getLossLimit(): number
    {
        return this.lossLimit
    }

    public checkLossLimitReached(): boolean
    {
        let result = this.getAutospinLoss() <= -this.getLossLimit() &&
            (this.getRegulationSettings().lossLimitRequired || this.getLossLimit() > 0);

        if(result) {
            this.autoSpinLimitReached = 'loss';
        }
        return result;
    }

    /**
     * Set loss limit for autospin used for UK market
     * @param limit {Number}
     */
    public setLossLimit(limit: number)
    {
        this.lossLimit = limit;
    }

    /**
     * Get win limit for autospin used for UK market
     * @returns {Number}
     */
    public getWinLimit(): number
    {
        return this.winLimit;
    }

    /**
     * Set win limit for autospin used for UK market
     * @returns {Number}
     */
    public setWinLimit(limit: number)
    {
        this.winLimit = limit;
    }

    public checkWinLimitReached(win: number): boolean
    {
        let result = win >= this.getWinLimit() && this.getWinLimit() > 0
        if(result) {
            this.autoSpinLimitReached = 'win';
        }
        return result;
    }

    public getTotalWinLimit(): number {
        return this.totalWinLimit;
    }

    public setTotalWinLimit(limit: number) {
        this.totalWinLimit = limit;
    }

    public checkTotalWinLimitReached(): boolean
    {
        let result = this.getCumulativeWins() >= this.totalWinLimit && this.totalWinLimit > 0;
        if(result) {
            this.autoSpinLimitReached = 'total_win';
        }
        return result;
    }

    /**
     * Losses since autospin started
     * @returns {Number}
     */
    public getAutospinLoss(): number
    {
        return BetModel.balance - this.autospinStartBalance;
    }

    /**
     * For total win. Accumulates all wins in current autoplay by default, can subtract losses as well.
     * @returns {Number}
     */
    public getCumulativeWins(subtractLosses: boolean = false) {
        let result = this.getAutospinLoss();
        if(!subtractLosses) {
            result += BetModel.totalBet * (this.initialAutospinNum - this.autoSpinNum);
        }
        return result;
    }

    /**
     * The values through which autospin cycles. Different for UK market
     * @returns {Array}
     */
    public getAutospinPresets(): number[]
    {
        return this.spinPresets;
    }

    /**
     * Get the maximum autospin number
     * @returns {Integer}
     */
    public getMaxAutospins()
    {
        const presets = this.spinPresets;
        return presets[presets.length - 1];
    }


    /**
     * Is autospinning enabled (i.e. Can you use it)
     * @returns {Boolean}
     */
    public autospinIsEnabled(): boolean
    {
        return this.autoSpinNum > 0 && !this.getRegulationSettings().lossLimitRequired || this.getLossLimit() > 0;
    }

    public toggleStopOnAnyWin() {
        if(this.stopOnAnyWin) {
            this.stopOnAnyWin = false;
        }
        else {
            this.stopOnAnyWin = true;
        }
    }
    public toggleStopOnBigWin() {
        if(this.stopOnBigWin) {
            this.stopOnBigWin = false;
        }
        else {
            this.stopOnBigWin = true;
        }
    }


    public checkStopOnBigWin(win: number) {
        let result = this.stopOnBigWin && win > GameModel.gameConfig.bigWinRatioLevels[0]*BetModel.totalBet;
        if(result) {
            this.autoSpinLimitReached = 'big_win';
        }
        return result;
    }

    public checkStopOnAnyWin(win: number) {
        let result = this.stopOnAnyWin && win > 0;
        if(result) {
            this.autoSpinLimitReached = 'any_win';
        }
        return result;
    }

    public toggleStopOnFeatureWin() {
        if(this.stopOnFeatureWin) {
            this.stopOnFeatureWin = false;
        }
        else {
            this.stopOnFeatureWin = true;
        }
    }

    public parseConfig = (data: any) => {
        Utils.serialize(data, this);
      }

    public getRegulationSettings(): AutoplaySettings {
        return this.regulationSettings[GameModel.regulation];
    }

    public clearLimits() {
        this.lossLimit = 0;
        this.totalWinLimit = 0;
        this.winLimit = 0;
        this.stopOnAnyWin = false;
        this.stopOnBigWin = false;
        this.stopOnFeatureWin = false;
    }
}

export default new AutoplayModel();

interface AutoplaySettings {
    lossLimitRequired: boolean;
    timeBetweenSpins?: number;
}
